import "../../../css/CrawlSources.css";
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useHistory, useParams } from "react-router-dom";
import { Table, Tag, message, Spin } from "antd";
import { getSource } from "../../../services/polly/crawlSourceService";
import { getJobs } from "../../../services/polly/crawlSourceService";
import { renderOutcome } from "./outcomeRenderers";

function formatDate(dateStr) {
  if (!dateStr) return "--";
  const d = new Date(dateStr);
  return d.toLocaleDateString() + " " + d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function formatDuration(startStr, endStr) {
  if (!startStr || !endStr) return "--";
  const ms = new Date(endStr) - new Date(startStr);
  if (ms < 0) return "--";
  const secs = Math.floor(ms / 1000);
  if (secs < 60) return `${secs}s`;
  const mins = Math.floor(secs / 60);
  const remainSecs = secs % 60;
  if (mins < 60) return `${mins}m ${remainSecs}s`;
  const hrs = Math.floor(mins / 60);
  const remainMins = mins % 60;
  return `${hrs}h ${remainMins}m`;
}

function triggerTag(trigger) {
  const map = {
    SCHEDULED: { color: "blue", label: "Scheduled" },
    MANUAL: { color: "purple", label: "Manual" },
    RETRY: { color: "orange", label: "Retry" },
  };
  const t = map[trigger] || { color: "default", label: trigger || "Unknown" };
  return <Tag color={t.color}>{t.label}</Tag>;
}

function statusTag(status) {
  const map = {
    RUNNING: { color: "blue", label: "Running" },
    COMPLETED: { color: "green", label: "Completed" },
    STOPPED: { color: "orange", label: "Stopped" },
    FAILED: { color: "red", label: "Failed" },
  };
  const s = map[status] || { color: "default", label: status || "Unknown" };
  return <Tag color={s.color}>{s.label}</Tag>;
}

export default function PastCrawls() {
  const { sourceId } = useParams();
  const history = useHistory();
  const [source, setSource] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const mountedRef = useRef(true);

  useEffect(() => {
    return () => { mountedRef.current = false; };
  }, []);

  // Load source name for breadcrumb
  useEffect(() => {
    (async () => {
      try {
        const data = await getSource(sourceId);
        if (mountedRef.current) setSource(data);
      } catch (err) {
        console.warn("Failed to load source:", err);
      }
    })();
  }, [sourceId]);

  const fetchJobs = useCallback(async (page, pageSize) => {
    setLoading(true);
    try {
      const res = await getJobs(sourceId, page, pageSize);
      if (!mountedRef.current) return;
      const list = Array.isArray(res) ? res : (res.content || []);
      const total = res.totalElements ?? list.length;
      setJobs(list);
      setPagination((prev) => ({ ...prev, current: page, pageSize, total }));
    } catch (err) {
      if (!mountedRef.current) return;
      console.warn("Failed to fetch crawl jobs:", err);
      message.error("Failed to load past crawls");
    }
    if (mountedRef.current) setLoading(false);
  }, [sourceId]);

  useEffect(() => {
    fetchJobs(1, pagination.pageSize);
  }, [fetchJobs]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTableChange = (pag) => {
    fetchJobs(pag.current, pag.pageSize);
  };

  const columns = [
    {
      title: "Started",
      dataIndex: "startedAt",
      key: "startedAt",
      width: 170,
      render: (d) => formatDate(d),
    },
    {
      title: "Duration",
      key: "duration",
      width: 120,
      render: (_, record) => formatDuration(record.startedAt, record.finishedAt),
    },
    {
      title: "Trigger",
      dataIndex: "trigger",
      key: "trigger",
      width: 110,
      render: (trigger) => triggerTag(trigger),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 110,
      render: (status) => statusTag(status),
    },
    {
      title: "Outcome",
      key: "outcome",
      render: (_, record) => renderOutcome(record),
    },
  ];

  const sourceName = source?.name || "Source";

  return (
    <div className="crawl-src-container">
      <div className="crawl-src-breadcrumb">
        <a onClick={() => history.push("/polly/crawl-sources")}>Crawl Sources</a>
        {" "}&rsaquo;{" "}
        <a onClick={() => history.push(`/polly/crawl-sources/${sourceId}/dashboard`)}>{sourceName}</a>
        {" "}&rsaquo;{" "}
        <span>Past Crawls</span>
      </div>
      <div className="crawl-src-body">
        <div className="crawl-src-header">
          <h2>Past Crawls</h2>
        </div>
        <Table
          columns={columns}
          dataSource={jobs}
          rowKey="id"
          loading={loading}
          size="middle"
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50"],
          }}
          onChange={handleTableChange}
          onRow={(record) => ({
            style: { cursor: "pointer" },
            onClick: () => {
              // Phase 3: link to job detail
              // history.push(`/polly/crawl-sources/${sourceId}/jobs/${record.id}`);
            },
          })}
        />
      </div>
    </div>
  );
}
